import {List, Map} from 'immutable';
import {CONFIG} from '../../appConfig';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import {getDateTimeObjFromTs} from './dateTime';
import {get, isObjectEmpty, getQsFromObj} from '../js/object';
import {types} from '../js/types';
import {STATE, DEEPLINK_QS} from '../../constants';

const EXTRA_STATUS = {
  OPTIONAL: 'OPTIONAL',
  WAIVED: 'WAIVED',
  MANDATORY: 'MANDATORY',
  INCLUDED: 'INCLUDED',
  CHARGED: 'CHARGED'
};

const TIME_TYPE = {
  PICKUP: 'PICKUP',
  RETURN: 'RETURN'
};

const AVAILABILITY_STATUS = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  AFTER: 'AFTER',
  UNAVAILABLE: 'UNAVAILABLE',
  EXCEEDS_MAX_DURATION: 'EXCEEDS_MAX_DURATION',
};

/**
 * @private
 * @desc Returns a contract object from GMA if one is present, or null
 * @param {String} contract_number
 * @param {Object} state An immutable state object
 * @returns {Object} {contract_number: string}
 */
function getContractDetailsFromGMA(contract_number, state) {
  let gmaContract = state.getIn(['gmi', 'gma', 'contract', 'details'].concat(contract_number), Map()).toJS();
  return !isObjectEmpty(gmaContract.contract_details) ? gmaContract.contract_details : {};
}

/**
 * @private
 * @desc Returns a contract object from Reservation object if one is present, or null
 * @param {String} contract_number
 * @param {Object} state An immutable state object
 * @returns {Object} {contract_number: string}
 */
function getContractDetailsFromReservation(contract_number, state) {
  const resContractNumber = state.getIn([...STATE.SESSION, 'gbo', 'reservation', 'contract_number'], '');
  return (resContractNumber === contract_number) ?
    state.getIn([...STATE.SESSION, 'gbo', 'reservation', 'contract_details'], Map()).toJS() :
    {};
}

/**
 * @name getContractDetails
 * @desc Returns a contract object; either from GBO, GMA or an object-wrapped contract_number
 * @param {string} contract_number
 * @param {Object} state An immutable state object
 * @returns {Object}
 * @memberof utils
 * @example
 * // WITHOUT a valid reservation object
 * const contract_number = 'MARLOW3';
 * const contract = utils.getContractDetails(contract_number);
 * console.log(contract); // {contract_number}
 *
 * // WITH a valid reservation object
 * const contract_number = 'MARLOW3';
 * // state must be immutableJS value with a reservation object located at state.gmi.session.gbo.reservation
 * state.gmi.session.gbo.reservation = Map({
 *  contract_number: 'MARLOW3',
 *  contract_details: {...}
 * });
 * const contract = utils.getContractDetails(contract_number, state);
 * console.log(contract); // state.gmi.session.gbo.reservation.contract_details
 *
 * // WITH a valid GMA object
 * const contract_number = 'MARLOW3';
 * // state must be immutableJS value with a details object located at state.gmi.gma.contract.details[contract_number]
 * state.gmi.gma.contract.details = Map({
 *  contract_details: {...}
 * });
 * const contract = utils.getContractDetails(contract_number, state);
 * console.log(contract); // state.gmi.gma.contract.details[contract_number].contract_details
 */
export function getContractDetails(contract_number, state) {
  return Object.assign(
    {contract_number},
    getContractDetailsFromGMA(contract_number, state),
    getContractDetailsFromReservation(contract_number, state)
  );
}

/**
 * @private
 * @desc Returns a coupon object from Reservation if one is present, or null
 * @param {String} code
 * @param {Array} coupons
 * @returns {Object} {code: string}
 */
function getCouponDetailsFromReservation(code, coupons = []) {
  const resCoupon = coupons.length ? coupons.filter((coupon) => coupon.code === code)[0] : {};
  return resCoupon ? resCoupon : {};
}

/**
 * @private
 * @desc Returns a coupon object from GMA if one is present, or null
 * @param {String} code
 * @param {Object} gmaCouponDetails An immutable details object
 * @returns {Object} {code: string}
 */
function getCouponDetailsFromGMA(code, gmaCouponDetails = Map()) {
  const gmaCoupon = gmaCouponDetails.get(code.toUpperCase());
  return gmaCoupon ? gmaCoupon.toJS() : {};
}

/**
 * @name getCouponsDetails
 * @desc Get a unified array of coupon object shapes
 * @param {Array} coupons
 * @param {Object} state
 * @returns {Array}
 * @memberof utils
 * @example
 * // WITHOUT a valid reservation object
 * let coupons = ['CURREN323'];
 * coupons = utils.getCouponsDetails(coupons);
 * console.log(coupons); // [{'CURREN323'}]
 *
 * // WITH a valid reservation object
 * const coupons = ['CURREN323'];
 * // reservation must be immutableJS value
 * state.gmi.session.gbo.reservation = fromJS({
 *  coupons: [{
 *    code: 'CURREN323',
 *    description: 'NMA CURRENCY OFF',
 *    quantity: 1,
 *    type: 'CURRENCY_OFF'
*    }]
 * });
 * let coupons = utils.getCouponsDetails(coupons, state);
 * console.log(coupons); // state.gmi.session.gbo.reservation.coupons
 *
 * // WITH a valid GMA object
 * const coupons = ['CURREN323'];
 * // state must be immutableJS value with a details object located at state.gmi.gma.coupon.details[coupons[0]]
 * state.gmi.gma.coupon.details = Map({
 *  code: 'CURREN323'
 * });
 * const coupons = utils.getCouponsDetails(coupons, state);
 * console.log(coupons); // state.gmi.gma.coupon.details[coupons[0]]
 */
export function getCouponsDetails(coupons = [], state) {
  const resCoupons = state.getIn([...STATE.SESSION, 'gbo', 'reservation', 'coupons'], List()).toJS();
  const GMACouponDetails = state.getIn(['gmi', 'gma', 'coupon', 'details'], Map());
  if (coupons.length) {
    return coupons.map((code) => {
      return Object.assign(
        {code},
        getCouponDetailsFromGMA(code, GMACouponDetails),
        getCouponDetailsFromReservation(code, resCoupons)
      );
    });
  }
}

/**
 * @name getRentersAgeOptions
 * @desc Get renter age array from either a branch location, or from a solr country code response
 * @param {Object} location
 * @param {Object} renter_age
 * @returns {Array}
 * @memberof utils
 * @example
 * const location = state.getIn(['gmi', 'ui', 'reservations', 'initiate', 'pickup_location']).toJS();
 *
 * // location is branch
 * const options = getRentersAgeOptions(location);
 *
 * // location is city
 * const renter_age = state.getIn(['gmi', 'solr', 'renter_age']).toJS();
 * const options = getRentersAgeOptions(location, renter_age);
 *
 * console.log(options); // [{age, value, selected}]
 */
export function getRentersAgeOptions(location, renter_age = Map()) {
  // future proof for potential is_branch bool
  if (location.is_branch === false || location.type === 'CITY') {
    return renter_age.get(get(location, 'address.country_code'), List()).toJS();
  } else {
    return get(location, 'additional_data.age_options');
  }
}

/**
 * @name isLocationAvailableByTime
 * @desc Takes in a SOLR location availability object and a comparable time object then returns if its open or closed
 * @param {Object} locationEligibility entire SOLR availability object
 * @param {Object} currentTime moment object for current time
 * @param {string} timeType PICKUP or RETURN
 * @param {string} timeZone of location
 * @returns {string} 'OPEN'|'CLOSED'|'AFTER'|'UNAVAILABLE'|'EXCEEDS_MAX_DURATION'
 *  string indicating whether this location is OPEN, CLOSED, UNAVAILABLE or AFTER hours at a particular time
 * @memberof utils
 * @example
 * const eligibility = {
 *   '2017-02-24': { STANDARD: { hours: [{ open: '08:00', close: '17:00' }] } },
 *   '2017-02-25': { STANDARD: { hours: [], open24Hours: false, closed: true } },
 *   '2017-02-26': { AFTER: { hours: [{ open: '18:00', close: '23:00' }] } }
 * };
 *
 * const shouldBeOpen = isLocationAvailableByTime(eligibility, moment({ M: '1', d: '24', y: '2017', hour: 8, minute: 30 }));
 * console.log(shouldBeOpen); // 'OPEN'
 *
 * const shouldBeClosed = isLocationAvailableByTime(eligibility, moment({ M: '1', d: '25', y: '2017', hour: 8, minute: 30 }));
 * console.log(shouldBeClosed); // 'CLOSED'
 *
 * const afterHoursAvailable = isLocationAvailableByTime(eligibility, moment({ M: '1', d: '26', y: '2017', hour: 18, minute: 30 }), 'PICKUP');
 * console.log(afterHoursAvailable); // 'AFTER'
 * 
 * const exceedsMaxDuration = isLocationAvailableByTime(eligibility, moment({ M: '1', d: '24', y: '2020', hour: 8, minute: 30 }), 'PICKUP', 'Europe/London', moment({ M: '1', d: '23', y: '2020', hour: 23, minute: 59 }));
 * console.log(exceedsMaxDuration); // 'EXCEEDS_MAX_DURATION'
 *
 * const shouldBeAvailable = isLocationAvailableByTime(eligibility, moment({ M: '1', d: '24', y: '2020', hour: 8, minute: 30 }), 'PICKUP', 'Europe/London');
 * Checks if the time given is behind the selected location's current time using it's timezone, so that we can show that the location is unavailable to pickup for that time as it's in past
 * console.log(shouldBeAvailable); // 'UNAVAILABLE' 
 */
export function isLocationAvailableByTime(locationEligibility, currentTime, timeType, timezone, maxRentalReturnDateTime) {
  // Base case for allowing all time slots when there is no location eligibility
  if (!locationEligibility) return AVAILABILITY_STATUS.OPEN;

  // If maxRentalReturnDateTime exist, we return Exceed max duration status
  const isExceedMaxDuration =
    !!maxRentalReturnDateTime
      ? currentTime.isAfter(maxRentalReturnDateTime)
      : false;
  if (isExceedMaxDuration) return AVAILABILITY_STATUS.EXCEEDS_MAX_DURATION;

  const eligibleDateTime = locationEligibility && locationEligibility[currentTime.format('YYYY-MM-DD')];

  // ECR-24461 :: Check if timezone is available and if it's the same day the date selected to check if the time slot is in past of location's current time 
  if (timezone &&
    moment(currentTime.format()).isSame(momentTimezone().tz(timezone).format(), 'day')
  ) {
    const localeTime = moment(currentTime).format('h:mma');
    const timeZoneTime = momentTimezone.tz(timezone).format('h:mma');
    // If the time slot is past the location's current time, need to show it as `unavailable`
    if (moment(localeTime, 'h:mma').isBefore(moment(timeZoneTime, 'h:mma'))) {
      return AVAILABILITY_STATUS.UNAVAILABLE;
    }
  }
  if (isOpenOnStandardTime(eligibleDateTime, currentTime)) return AVAILABILITY_STATUS.OPEN;
  if (isAfterHours(eligibleDateTime, currentTime, timeType)) return AVAILABILITY_STATUS.AFTER;

  return AVAILABILITY_STATUS.CLOSED;
}

/**
 * @private
 * @name isOpenOnStandardTime
 * @desc Determines if location is open on standard time
 * @param {Object} eligibleTime time range for the eligible date
 * @param {Object} currentTime moment object for current time
 * @returns {boolean} indicates if the location is open during standard hours
 * @memberof utils
 */
function isOpenOnStandardTime(eligibleTime, currentTime) {
  const standardHours = get(eligibleTime, 'STANDARD.hours') || [];
  const isClosed = get(eligibleTime, 'STANDARD.closed'); // determines if the location is closed all day long
  const is24hours = get(eligibleTime, 'STANDARD.open24Hours');
  const noStandardHours = !standardHours || !standardHours.length;

  return (
    !isClosed &&
    (noStandardHours || (is24hours || isTimeInRange(currentTime, standardHours)))
  );
}

/**
 * @private
 * @name isAfterHours
 * @desc Determines if location is open during after hours
 * @param {Object} eligibleTime time range for the eligible date
 * @param {Object} currentTime moment object for current time
 * @param {string} timeType PICKUP or RETURN
 * @returns {boolean} indicates if the location is open during after hours
 * @memberof utils
 */
function isAfterHours(eligibleTime, currentTime, timeType) {
  /**
   * After hours:
   * PICKUP - The after hours will come in AFTER.hours
   * RETURN - The after hours will come in DROP.hours
   */
  const afterHours = get(eligibleTime, 'AFTER.hours') || [];
  const dropHours = get(eligibleTime, 'DROP.hours') || [];
  const is24hoursAfterHourDrop = get(eligibleTime, 'DROP.open24Hours');
  const is24hoursAfterHourPickup = get(eligibleTime, 'AFTER.open24Hours');

  return (
    (timeType === TIME_TYPE.PICKUP &&
      (!!is24hoursAfterHourPickup || isTimeInRange(currentTime, afterHours))) ||
    (timeType === TIME_TYPE.RETURN &&
      (!!is24hoursAfterHourDrop || isTimeInRange(currentTime, dropHours)))
  );
}

/**
 * @name isTimeInRange
 * @desc Checks if a time is in the hours array
 * @param {Object} time The time to check for
 * @param {Array} hours Array with the hours information from a location
 * @returns {boolean} indicating whether the time is within one of available ranges
 * @memberof utils
 * @example
 * const time = moment({ M: '1', d: '24', y: '2017', hour: 16, minute: 59 });
 * const range = [{ open: '02:00', close: '08:00' }, { open: '13:00', close: '17:00' }];
 *
 * const result = isTimeInRange(time, range);
 * console.log(result); // true
 */
export function isTimeInRange(time, hours = []) {
  const begin = time.clone(),
    end = time.clone();

  for (let i = 0; i < hours.length; i++) {
    const openHoursMinutes = hours[i].open.split(':');
    const closeHoursMinutes = hours[i].close.split(':');
    if (
      time >=
        begin.set({ h: openHoursMinutes[0], m: openHoursMinutes[1] }).valueOf() &&
      time <= end.set({ h: closeHoursMinutes[0], m: closeHoursMinutes[1] }).valueOf()
    ) {
      return true;
    }
  }

  return false;
}

/**
 * @name isLocationAvailableByDate
 * @desc Takes in a SOLR location availability object and a comparable date object then returns if its open or closed
 * @param {Object} locationEligibility entire SOLR availability object
 * @param {Object} currentDate moment object for current date
 * @param {string} timeType PICKUP or RETURN
 * @returns {boolean} whether this location is open at a particular date
 * @memberof utils
 */
export function isLocationAvailableByDate(locationEligibility, currentDate, timeType) {
  let open = true;
  const eligibleDateTime =
    locationEligibility && locationEligibility[currentDate.format('YYYY-MM-DD')];

  if (eligibleDateTime) {
    const isStandardClosed = get(eligibleDateTime, 'STANDARD.closed');
    const isAfterHoursReturnEligible = timeType === TIME_TYPE.RETURN && eligibleDateTime.DROP;
    const isAfterHoursPickupEligible = timeType === TIME_TYPE.PICKUP && eligibleDateTime.AFTER;

    if (isStandardClosed) {
      open = false;
    }
    if ((isAfterHoursReturnEligible || isAfterHoursPickupEligible) && isStandardClosed) {
      open = true;
    }
  }
  return open;
}


/**
  * @name isLocationAvailableByDateTime
  * @desc Aggregate utility that takes in a SOLR location availability object, a date time object and string to indicate
  *  whether the time is PICKUP or RETURN and returns if its open or closed
  * @param {Object} locationEligibility entire SOLR availability object
  * @param {Object} currentTime moment object for current time
  * @param {string} timeType PICKUP or RETURN
  * @returns {Boolean} whether this location is open at a particular date and time
  * @memberof utils
  */
export function isLocationAvailableByDateTime(locationEligibility, currentDateTime, timeType) {
  return isLocationAvailableByDate(locationEligibility, currentDateTime, timeType)
    && isLocationAvailableByTime(locationEligibility, currentDateTime, timeType);
}


/**
 * @name getReservationDeepLinkParams
 * @desc Get an object of CORPORATE formatted deep link query params, to be passed to gmi deeplink service
 * @see getReservationDeepLinkQs for param examples
 * @returns {Object}
 * @memberof utils
 */
export function getReservationDeepLinkParams(reservation, stop) {
  const {
    IATA_NUMBER,
    CASH_IN_CLUB_NUMBER,
    RENTER_AGE,
    REDEMPTION_DAY_COUNT,
    PARTNER_REWARD_PROGRAM_CODE,
    PARTNER_REWARD_PROGRAM_MEMBER_ID,
    STOP,
    IGNORE_CONTRACT_FROM_PROFILE,
    SHOW_CASH_IN_CLUB_NUMBER,
    SHOW_PRODUCT_CODE,
    PRODUCT_CODE,
    BOOKING_AGENT,
    LAST_NAME,
    MEMBERSHIP_ID,
    LOYALTY_BRAND,
    PICKUP_LOCATION_ID,
    RETURN_LOCATION_ID,
    PICKUP_DATE,
    PICKUP_TIME,
    RETURN_DATE,
    RETURN_TIME,
    CONTRACT_NUMBER,
    COUPONS,
    ADDITIONALINFO,
    ONE_WAY_RENTAL,
    PREPAY_SELECTED,
    LOCALE,
    CAR_CLASS_CODE,
    EXTRAS,
    FIRSTNAME,
    EMAIL,
    PHONENUMB,
    BILLINGNUMBER
  } = DEEPLINK_QS;

  const qsParamsWithExactName = [
    IATA_NUMBER,
    CASH_IN_CLUB_NUMBER,
    RENTER_AGE,
    REDEMPTION_DAY_COUNT,
    PARTNER_REWARD_PROGRAM_CODE,
    PARTNER_REWARD_PROGRAM_MEMBER_ID,
    IGNORE_CONTRACT_FROM_PROFILE,
    SHOW_CASH_IN_CLUB_NUMBER,
    SHOW_PRODUCT_CODE,
    PRODUCT_CODE,
    CAR_CLASS_CODE
  ];

  const qs = { v: '2' };

  // Gets each deeplink const that matches the preflight property and pushes it to the qs.
  qsParamsWithExactName.forEach(param => {
    if (reservation[param]) qs[param] = reservation[param];
  });

  // LDT
  if (reservation.pickup_location) {
    qs[PICKUP_LOCATION_ID] = (reservation.pickup_location.id || reservation.pickup_location.peopleSoftId);
  }
  if (reservation.return_location) {
    qs[RETURN_LOCATION_ID] = (reservation.return_location.id || reservation.return_location.peopleSoftId);
  }

  // Ideally both pickup and return date and time could be sent like:
  // qs[RETURN_TIME] = reservation.return_time;
  // Both date and time into one parameter, like we do for initiate.
  // But, right now, GMA shared AND Enterprise handle these infos by
  // receiving two seperate params, merging them, and then sending to
  // GBO, so we can't update this unless both GMA codes start accepting
  // already merged infos.
  if (reservation.pickup_time) {
    const pickupTime = getDateTimeObjFromTs(reservation.pickup_time);
    qs[PICKUP_DATE] = pickupTime.format('M/D/YYYY');
    qs[PICKUP_TIME] = pickupTime.format('HH:mm');
  }
  if (reservation.return_time) {
    const returnTime = getDateTimeObjFromTs(reservation.return_time);
    qs[RETURN_DATE] = returnTime.format('M/D/YYYY');
    qs[RETURN_TIME] = returnTime.format('HH:mm');
  }

  // We shouldn't deeplink using the default loyalty contract.
  // In case the user is reinitiating the reservation, the contract from profile should be re-applied.
  // NCR-10556
  const isDefaultLoyaltyContract = get(
    reservation,
    'contract_details.default_loyalty_contract'
  );

  // if it's '', include query param so BE knows not to initiate with contract on profile
  const hasContract =
    reservation.contract_number ||
    reservation.contract_number === '' ||
    get(reservation, 'contract_details.contract_number') ||
    !!reservation.ignore_contract_from_profile;
  // If we get ignore_contract_from_profile, we need to make sure hasContract is true so it adds an
  // empty string as contract qs in case contract number wasn't informed, removing any possible
  // default profile contract.

  if (hasContract && !isDefaultLoyaltyContract) {
    const contractNumber =
      reservation.contract_number ||
      get(reservation, 'contract_details.contract_number') ||
      '';

    qs[CONTRACT_NUMBER] = contractNumber;
  }

  if (reservation.coupons) {
    let coupons = reservation.coupons;
    if (types(reservation.coupons[0]).isObject) {
      coupons = reservation.coupons.map(coupon =>
        // Coupons are grouped by quantity by GBO so here we need
        // to split each of the coupons into a single string in case
        // its quantity is > 1.
        Array.from(Array(coupon.quantity || 1)).map(() => coupon.code)
      );
    }

    qs[COUPONS] = coupons.join(',');
  }

  if (reservation.booking_agent) {
    const bookingAgentQsParamsWithExactName = [
      BOOKING_AGENT,
      LAST_NAME,
      MEMBERSHIP_ID,
      LOYALTY_BRAND
    ];

    // Gets each booking agent related deeplink const that matches the reservation property
    // and add it to the qs.
    bookingAgentQsParamsWithExactName.forEach(param => {
      if (reservation[param]) qs[param]=reservation[param];
    });
  }

  // CAR CLASS
  if (reservation.car_class_details) {
    const {car_class_details} = reservation;
    if (car_class_details.code) {
      qs[CAR_CLASS_CODE] = car_class_details.code;
    }
    // EXTRAS
    const extras = get(car_class_details, 'vehicle_rates[0].extras');
    if (extras) {
      Object.keys(extras).forEach(type => {
        extras[type].forEach(extra => {
          const {selected_quantity} = extra;
          const isValid = types(selected_quantity).isNumber && selected_quantity !== 0 && extra.status !== EXTRA_STATUS.MANDATORY;
          if (isValid) {
            qs[`${EXTRAS}.${type}.${extra.code}`] = selected_quantity;
          } // Example: extras.equipment.GPS=1
        });
      });
    }
  }

  qs[ONE_WAY_RENTAL] = !!reservation.one_way_rental;
  qs[PREPAY_SELECTED] = !!reservation.prepay_selected;

  const {driver_info} = reservation;
  if (driver_info) {
    if (driver_info.first_name) qs[FIRSTNAME] = driver_info.first_name;
    if (driver_info.last_name) qs[LAST_NAME] = driver_info.last_name;
    if (driver_info.email_address) qs[EMAIL] = driver_info.email_address;
    const phone_number = get(driver_info, 'phone.phone_number');
    if (phone_number) qs[PHONENUMB] = phone_number;
  }
  if (reservation.billing_account) qs[BILLINGNUMBER] = reservation.billing_account;
  if (reservation.additional_information) {
    // Additional Information params are:
    // additionalInfo, additionalInfo2, additionalInfo3, additionalInfo4, additionalInfo5
    reservation.additional_information.forEach((item, i) => {
      const isValidValue = types(item.value).isDefined && !types(item.value).isNull;

      if (isValidValue) {
        qs[`${ADDITIONALINFO}${(i === 0 ? '' : i+1)}`] = item.value;
      }
    });
  }

  if (stop) qs[STOP] = stop;
  qs[LOCALE] = `${CONFIG.LANGUAGE}_${CONFIG.COUNTRY}`;
  return qs;
}

/**
 * @name getReservationDeepLinkQs
 * @desc Get a CORPORATE formatted deep link query string
 * @param {Object} reservation LDT or GBO res object (can merge commitState into res object as well)
 * @param {String} [stop] indicates how far should GMA call actual GBO services
 * @returns {String}
 * @memberof utils
 * @example
 * // you can get a deep links query string just based on LDT data
 * const ldt = {
 *  one_way_rental: false,
 *  pickup_location: {id: 456, countryCode:'FR'},
 *  return_location: {id: 456, countryCode:'FR'},
 *  pickup_time: '2017-03-01T12:00',
 *  return_time: '2017-03-04T12:00',
 *  renter_age: '25',
 *  contract_number: 'marlow1',
 *  coupons: ['CURREN323']
 * };
 * // stop indicates how far should GMA call actual GBO services
 * const stop = 'book';
 * const qs = utils.getReservationDeepLinkQs(ldt, stop));
 * console.log(qs);
 * // ?v=2&pickUpLocation.searchCriteria=456&dropOffLocation.searchCriteria=456&
 * // pickUpDateTime.date=3/1/2017&pickUpDateTime.time=12:00&dropOffDateTime.date=3/4/2017&dropOffDateTime.time=12:00&
 * // age=25&contractNumber=marlow1&coupons=CURREN323&car_class_code=CCAR&oneway=false&prepay_selected=false&stop=book&locale=en_US
 *
 * // or you can use a gbo res object, and even merge with commitState data
 * const reservation = Object.assign({}, ldt, {
 *  coupons: [{code:'CURREN323'}],
 *  car_class_details: {
 *    code: 'CCAR',
 *    vehicle_rates: [{
 *      extras: {
 *        equipment: [{code: 'CSI', selected_quantity:2}],
 *        insurance: [{code: 'RSP', selected_quantity:1}],
 *        fuel: [{code:'FOO', selected_quantity:0}]
 *      }
 *    }]
 *  }
 * });
 * const commitState = {
 *  driver_info: {
 *    first_name: 'TEST',
 *    last_name: 'TESTER',
 *    email_address: 'test123@test123.com',
 *    phone: {phone_number: '1231231234'},
 *  },
 *  billing_account: '12349861',
 *  additional_information: [
 *    {id: '22518482397997', value: '2018-09-19T22:57:57.000Z'},
 *    {id: '22518482397997', value: '2018-09-21T22:57:57.000Z'}
 *  ]
 * };
 * const stop = 'REVIEW';
 * const qs = utils.getReservationDeepLinkQs(Object.assign({}, reservation, commitState), stop));
 * console.log(qs);
 * // ?v=2&pickUpLocation.searchCriteria=456&dropOffLocation.searchCriteria=456&
 * // pickUpDateTime.date=3/1/2017&pickUpDateTime.time=12:00&dropOffDateTime.date=3/4/2017&dropOffDateTime.time=12:00&
 * // age=25&contractNumber=marlow1&coupons=CURREN323&car_class_code=CCAR&extras.equipment.CSI=2&extras.insurance.RSP=1&oneway=false&
 * // prepay_selected=false&firstname=TEST&last_name=TESTER&email=test123@test123.com&phoneNumb=1231231234&
 * // billingNumber=12349861&additionalInfo=2018-09-19T22:57:57.000Z&additionalInfo2=2018-09-21T22:57:57.000Z&stop=review&locale=en_US
 */
export function getReservationDeepLinkQs(reservation, stop) {
  const qsObj = getReservationDeepLinkParams(reservation, stop);
  return getQsFromObj(qsObj);
}

/**
 * @private
 * @desc Group extras by status
 * @param {Array} extras
 * @returns {Object} {all, charged, included, mandatory, optional, selected, waived}
 */
function enrichExtras(extras = []) {
  const all = extras;
  const included = [], optional = [], mandatory = [], waived = [], charged = [], selected = [];
  all.forEach(extra => {
    extra.status === EXTRA_STATUS.CHARGED && charged.push(extra);
    extra.status === EXTRA_STATUS.INCLUDED && included.push(extra);
    extra.status === EXTRA_STATUS.MANDATORY && mandatory.push(extra);
    extra.status === EXTRA_STATUS.OPTIONAL && optional.push(extra);
    extra.status === EXTRA_STATUS.WAIVED && waived.push(extra);
    extra.selected_quantity > 0 && selected.push(extra);
  });

  return {all, charged, included, mandatory, optional, selected, waived};
}

/**
 * @name getFormattedReservationExtras
 * @desc Enrich extras object with nested arrays based on status or concatenate types based on status. Useful for building UIs that require extras to be grouped by type and/or status
 * @param {Object} opts {
 *   extras: {equipment, fuel, insurance},
 *   [status]: 'all|charged|included|mandatory|optional|selected|waived'
 * }
 * @returns {Array|Object}
 * @memberof utils
 * @example
 * // Enrich extra types with nested status arrays
 * // Use in conjunction with utils.getVehicleRatesFromReservation
 * utils.getFormattedReservationExtras({extras})
 * // return {
 * //   equipment: {all: [], charged: [], included: [], mandatory: [], optional: [], selected: [], waived: []},
 * //   fuel: {all: [], charged: [], included: [], mandatory: [], optional: [], selected: [], waived: []},
 * //   insurance: {all: [], charged: [], included: [], mandatory: [], optional: [], selected: [], waived: []}
 * // }
 *
 * // Concatenate extra types based on status
 * utils.getFormattedReservationExtras({extras, status: 'all'})
 * utils.getFormattedReservationExtras({extras, status: 'charged'})
 * utils.getFormattedReservationExtras({extras, status: 'included'})
 * utils.getFormattedReservationExtras({extras, status: 'mandatory'})
 * utils.getFormattedReservationExtras({extras, status: 'optional'})
 * utils.getFormattedReservationExtras({extras, status: 'waived'})
 * utils.getFormattedReservationExtras({extras, status: 'selected'})
 * // return [extra, extra, extra]
 */
export function getFormattedReservationExtras({extras, status}) {
  const equipment = enrichExtras(extras['equipment']);
  const fuel = enrichExtras(extras['fuel']);
  const insurance = enrichExtras(extras['insurance']);

  if (types(status).isDefined) {
    return [].concat(equipment[status], fuel[status], insurance[status]);
  } else {
    return {equipment, fuel, insurance};
  }
}

/**
 * @name getVehicleRatesFromReservation
 * @desc DRY: Gets a vehicle rates object by charge type from the reservation, contains extras and price summary
 * @param {Object} opts {
 *   reservation: object,
 *   [chargeType]: string
 * }
 * @returns {Object} {charge_type, extras, price_summary, supplemental}
 * @memberof utils
 * @example
 * utils.getVehicleRatesFromReservation({reservation})
 * utils.getVehicleRatesFromReservation({reservation, chargeType: 'PAYLATER'})
 */
export function getVehicleRatesFromReservation({reservation, chargeType = 'PAYLATER'}) {
  const vehicleRates = get(reservation, 'car_class_details.vehicle_rates') || [];
  return vehicleRates[vehicleRates.findIndex((item) => item.charge_type === chargeType)] || {};
}

/**
 * @private
 * @desc Useful for setting gmi.ui.reservations.extras to reflect the state of the reservation
 * @param {Object} opts {
 *   reservation: object,
 *   [chargeType]: string
 * }
 * @returns {Array}
 * @example
 * // Exported for use in actions.reservation.modifyStart and actions.reservation.extrasUpdate
 * getSelectedExtrasFromReservation({reservation})
 * getSelectedExtrasFromReservation({reservation, chargeType: 'PAYLATER'})
 * // return [extra, extra, extra]
 */
export function getSelectedExtrasFromReservation({reservation, chargeType}) {
  const vehicleRates = getVehicleRatesFromReservation({reservation, chargeType});
  const {extras} = vehicleRates;

  if (types(extras).isObject) {
    return getFormattedReservationExtras({extras, status: 'selected'})
      .map(({code, name, selected_quantity}) => ({code, name, quantity: selected_quantity}));
  } else {
    return [];
  }
}

/**
 * @private
 * @desc Gets the path and value for the create or update set operation of additional information
 * @param {Object} opts {
 *   info: {Object},
 *   state: {Object},
 *   basePath: {Array}
 * }
 * @returns {Object} {
 *   path: {Array},
 *   value: {Object|Array}
 * }
 */
export function getAdditionalInfoSetOpts({info, state, basePath}) {
  // Default path and value, update later if setting info: Object and additional info already exists in the store
  let path = basePath;
  let value = [info];

  // Check whats in the store
  const resAdditionalInfo = state.getIn(basePath, false);
  if (resAdditionalInfo) {
    // Determine where to set the object
    let index = resAdditionalInfo.findIndex(resInfo => resInfo.get('id') === info.id);
    if (index === -1) index = resAdditionalInfo.size;

    // update path and value
    path = path.concat([index.toString()]);
    value = info;
  }

  return {path, value};
}


/**
 * @private
 * @desc Gets the path to the to-be-removed additional information
 * @param {Object} opts {
 *   id: string,
 *   state: {Object},
 *   basePath: {Array}
 * }
 * @returns {Array}
 */
export function getAdditionalInfoUnsetPath({id, state, basePath}) {
  if (id) {
    const resAdditionalInfo = state.getIn(basePath, []);
    const index = resAdditionalInfo.findIndex(resInfo => resInfo.get('id') === id);
    if (index !== -1) {
      return basePath.concat([index.toString()]);
    }
  } else {
    return basePath;
  }
}

/**
 * @private
 * @name getVehicleSelectLabelByCode
 * @desc Parse the car_class_filter filter_values to find a user-friendly description/label for a numerical string code
 * @param {Array} car_classes_filters
 * @param {String} code
 * @returns {String}
 */
export function getVehicleSelectLabelByCode(car_classes_filters, code) {
  for (let i = 0; i < car_classes_filters.length; i++) {
    const filter_values = car_classes_filters[i].filter_values;
    for (let j = 0; j < filter_values.length; j++) {
      const filter_value = filter_values[j];
      if (filter_value.code === code) return filter_value.description;
    }
  }
}

/**
 * @name getTimeFrames
 * @desc Creates a range of hours before and after a given date.
 * @param {object[]} locationEligibility - SOLR shaped hours used to check if frames are available
 * @param {object} openCloseRange - open close hour range used to build the time frames
 * @param {number} frameInterval - Time interval for the time frames
 * @param {object} pickupOpenCloseRange - open close hour range for pickup
 * @param {object} dropoffOpenCloseRange - open close hour range for dropoff
 * @param {string} type - PICKUP or RETURN
 * @param {string} timezone - selected location's timezone to check if time is in past to the location's current time
 * @param {object} maxRentalReturnDateTime - moment time object for maximum rental return, if exist
 * @returns {object[]} - List of time frames representing open & closed location times
 * @memberof utils
 */
export const getTimeFrames = ({
  locationEligibility,
  openCloseRange = {},
  frameInterval = 30,
  pickupOpenCloseRange,
  dropoffOpenCloseRange,
  type,
  timezone,
  maxRentalReturnDateTime = false
}) => {
  const initialTime = openCloseRange.min;
  // This multiplies the range by four so we have the number of half-hours
  // before and after the given range and adds one for current hour.
  const range = getRangeInHours(openCloseRange, frameInterval);
  // Moment date to manipulate for each loop
  let momentDate = moment(initialTime);

  const timeFrames = [];
  // Builds the list of `timeFrames`
  for (let i = 0; i < range; i++) {
    // Returns location status (OPEN|CLOSE|UNAVAILABLE|EXCEEDS_MAX_DURATION) for the given time
    const locationStatus = isLocationAvailableByTime(locationEligibility, momentDate, type, timezone, maxRentalReturnDateTime);
    // Returns if the current time frame is within the pickup times range
    const isInsidePickupHours = isTimeInRange(momentDate, pickupOpenCloseRange);
    // Returns if the current time frame is within the dropoff times range
    const isInsideDropoffHours = isTimeInRange(momentDate, dropoffOpenCloseRange);

    const timeFrame = {
      dateTime: momentDate.format(), // Moment Date Time format
      locationStatus, // location is open/close for the time
      pickupTimeAvailable: isInsidePickupHours, // time is within the pickup times range
      dropoffTimeAvailable: isInsideDropoffHours, // time is within the dropoff times range
      time: momentDate.format('HH:mm') // Formats to 24Hour time
    };

    timeFrames.push(timeFrame);

    // Increments momentDate before next iteraction
    momentDate = momentDate.clone().add(frameInterval, 'minutes');
  }

  return timeFrames;
};

/**
 * @name getRangeInHours
 * @desc Returns a time range in given time interval. This is needed to create the
 * time frames as they are created with the interval.
 *
 * Example:
 * getRangeInHours({
     min: '2020-05-27T09:00',
 *   max: '2020-05-27T11:00'
 *  }, 30)
 *
 * The difference would be 2 full hours, but the array should include:
 * 09:00, 09:30, 10:00, 10:30, 11:00 (5 total)
 *
 * @param {object} adjustHours - adjust hours object
 * @param {number} timeInterval - Time interval for the time frames
 * @returns {number} - The time range in given time interval
 * @memberof utils
 */
export const getRangeInHours = ({ min, max }, timeInterval) => {
  const start = moment(min);
  const end = moment(max);
  const timeDiffInMiliseconds = start.diff(end);
  // Converts `timeDiffInMiliseconds` to hours
  const timeDiffInHours = timeDiffInMiliseconds / 60 / 60 / 1000;
  // Time interval frames for an hour
  const numberOfIntervals = 60/timeInterval;

  // Multiplies the time difference by numberOfIntervals so we have the range
  // and include the last interval
  const range = Math.abs(timeDiffInHours) * numberOfIntervals + 1;

  /**
   * We round the range down for cases in which we have the end time as `11:59`.
   * This would result in a range of `9.96`, for example. And since the range
   * represents the number of complete half-hours, rounding down will exclude that
   * that last incomplete one.
   */
  return Math.floor(range);
};

/**
 * @name getTimeWithMeridiem
 * @desc Formats a given time returning an object containing hour and ampm information.
 *
 * @param {string} time - Given time
 * @param {object} i18nUnits - AEM i18n units for hours/minutes/ampm
 * @returns {object} - Object with hour with am/pm
 * @memberof utils
 */
export const getTimeWithMeridiem = (time, i18nUnits) => {
  // If no time is passed, return empty strings so current time is not rendered
  if (!time) return { hour: '', ampm: '' };

  const {hour, minute, ampm} = i18nUnits;
  const momentDate = moment(time);
  const hourFormat = `${hour}:${minute}`;
  return {
    hour: momentDate.format(hourFormat),
    ampm: ampm !== 'false' && momentDate.format(ampm)
  };
};

/**
 * @name formatTimeStamp
 * @desc Get the current timestamp based on exchange type,
 * Format timestamp based on what the current value in state
 * @param {Object} [opts] {ts: String, state: Object, part: String}
 * @returns {Object}
 */
export const formatTimeStamp = (opts) => {
  const {part, path} = opts;
  let {ts = ''} = opts;
  const stateTs = opts.state.getIn(path) || '';
  const stateTsArr = stateTs ? stateTs.split('T') : ['', ''];

  if (stateTsArr.length === 2) {
    // replace first
    if (part === 'date') {
      stateTsArr.splice(0, 1, ts);
    }
    // replace last
    if (part === 'time') {
      stateTsArr.splice(1, 1, ts);
    }
  }

  ts = stateTsArr.join('T');

  return {
    ts,
    isNewTs: ts !== stateTs
  };
}